import { supabase } from '../lib/supabase';

export const importService = {
  // Parse CSV file
  parseCSV(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result;
          const lines = text?.split('\n')?.filter(line => line?.trim());
          
          if (!lines || lines?.length === 0) {
            reject(new Error('Empty file'));
            return;
          }
          
          // Parse header
          const headers = lines[0]?.split(',')?.map(h => h?.trim()?.replace(/"/g, ''));
          
          // Parse rows
          const rows = [];
          for (let i = 1; i < lines?.length; i++) {
            const values = lines[i]?.split(',')?.map(v => v?.trim()?.replace(/"/g, ''));
            const row = {};
            headers?.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            rows.push(row);
          }
          
          resolve({ headers, rows });
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  },

  // Validate lead data
  validateLead(leadData) {
    const errors = [];
    
    // Required fields
    if (!leadData?.nombre || leadData?.nombre?.trim() === '') {
      errors?.push('Name is required');
    }
    
    if (!leadData?.email || leadData?.email?.trim() === '') {
      errors?.push('Email is required');
    } else if (!this.isValidEmail(leadData?.email)) {
      errors?.push('Invalid email format');
    }
    
    // Optional phone validation
    if (leadData?.telefono && !this.isValidPhone(leadData?.telefono)) {
      errors?.push('Invalid phone format');
    }
    
    // Budget validation
    if (leadData?.presupuesto && (isNaN(leadData?.presupuesto) || leadData?.presupuesto < 0)) {
      errors?.push('Invalid budget amount');
    }
    
    return {
      valid: errors?.length === 0,
      errors
    };
  },

  // Email validation
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  },

  // Phone validation (basic)
  isValidPhone(phone) {
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    return phoneRegex?.test(phone?.replace(/\s/g, ''));
  },

  // Check for duplicate leads
  async checkDuplicates(email, phone) {
    try {
      const { data, error } = await supabase
        ?.rpc('find_duplicate_leads', {
          check_email: email,
          check_phone: phone || null
        });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return [];
    }
  },

  // Import leads from CSV
  async importLeads(file, advisorId, fieldMapping, options = {}) {
    try {
      // Parse CSV
      const { headers, rows } = await this.parseCSV(file);
      
      // Create import record
      const { data: importRecord, error: importError } = await supabase
        ?.from('lead_imports')
        ?.insert({
          imported_by: advisorId,
          file_name: file?.name,
          total_rows: rows?.length,
          status: 'processing',
          import_settings: {
            fieldMapping,
            options
          }
        })
        ?.select()
        ?.single();
      
      if (importError) throw importError;
      
      const results = {
        successful: 0,
        failed: 0,
        duplicates: 0,
        errors: []
      };
      
      // Process each row
      for (let i = 0; i < rows?.length; i++) {
        const row = rows?.[i];
        
        try {
          // Map fields
          const leadData = {
            nombre: row?.[fieldMapping?.nombre] || '',
            email: row?.[fieldMapping?.email] || '',
            telefono: row?.[fieldMapping?.telefono] || '',
            fuente: row?.[fieldMapping?.fuente] || options?.defaultSource || 'other',
            proyectoInteres: row?.[fieldMapping?.proyectoInteres] || options?.defaultProject || 'Residencial 3 Volcanes',
            presupuesto: parseFloat(row?.[fieldMapping?.presupuesto]) || null,
            nivelInteres: row?.[fieldMapping?.nivelInteres] || 'warm',
            notas: row?.[fieldMapping?.notas] || `Imported from ${file?.name}`
          };
          
          // Validate
          const validation = this.validateLead(leadData);
          if (!validation?.valid) {
            results.failed++;
            results?.errors?.push({
              row: i + 2,
              email: leadData?.email,
              errors: validation?.errors
            });
            continue;
          }
          
          // Check duplicates
          if (options?.checkDuplicates) {
            const duplicates = await this.checkDuplicates(leadData?.email, leadData?.telefono);
            if (duplicates?.length > 0) {
              results.duplicates++;
              results?.errors?.push({
                row: i + 2,
                email: leadData?.email,
                errors: ['Duplicate lead found']
              });
              continue;
            }
          }
          
          // Insert lead
          const { error: insertError } = await supabase
            ?.from('crm_leads')
            ?.insert({
              advisor_id: advisorId,
              nombre: leadData?.nombre,
              email: leadData?.email,
              telefono: leadData?.telefono,
              fuente: leadData?.fuente,
              proyecto_interes: leadData?.proyectoInteres,
              presupuesto: leadData?.presupuesto,
              stage: 'new',
              nivel_interes: leadData?.nivelInteres,
              notas: leadData?.notas,
              import_batch_id: importRecord?.id,
              campaign_id: options?.campaignId || null,
              utm_source: options?.utmSource || null,
              utm_medium: options?.utmMedium || null,
              utm_campaign: options?.utmCampaign || null
            });
          
          if (insertError) {
            results.failed++;
            results?.errors?.push({
              row: i + 2,
              email: leadData?.email,
              errors: [insertError?.message]
            });
          } else {
            results.successful++;
          }
        } catch (rowError) {
          results.failed++;
          results?.errors?.push({
            row: i + 2,
            errors: [rowError?.message]
          });
        }
      }
      
      // Update import record
      await supabase
        ?.from('lead_imports')
        ?.update({
          successful_imports: results?.successful,
          failed_imports: results?.failed,
          duplicates_found: results?.duplicates,
          status: results?.failed === 0 ? 'completed' : results?.successful > 0 ? 'partial' : 'failed',
          error_log: results?.errors,
          completed_at: new Date()?.toISOString()
        })
        ?.eq('id', importRecord?.id);
      
      return {
        importId: importRecord?.id,
        ...results
      };
    } catch (error) {
      console.error('Error importing leads:', error);
      throw error;
    }
  },

  // Export leads to CSV
  async exportLeads(filters = {}) {
    try {
      let query = supabase?.from('crm_leads')?.select('*');
      
      // Apply filters
      if (filters?.advisorId) {
        query = query?.eq('advisor_id', filters?.advisorId);
      }
      if (filters?.stage) {
        query = query?.eq('stage', filters?.stage);
      }
      if (filters?.fuente) {
        query = query?.eq('fuente', filters?.fuente);
      }
      if (filters?.campaignId) {
        query = query?.eq('campaign_id', filters?.campaignId);
      }
      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }
      
      const { data, error } = await query?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Convert to CSV
      const headers = ['ID', 'Name', 'Email', 'Phone', 'Source', 'Project', 'Budget', 'Stage', 'Interest Level', 'Created At'];
      const csvRows = [headers?.join(',')];
      
      data?.forEach(lead => {
        const row = [
          lead?.id,
          `"${lead?.nombre}"`,
          lead?.email,
          lead?.telefono || '',
          lead?.fuente,
          `"${lead?.proyecto_interes || ''}"`,
          lead?.presupuesto || '',
          lead?.stage,
          lead?.nivel_interes,
          new Date(lead?.created_at)?.toISOString()
        ];
        csvRows?.push(row?.join(','));
      });
      
      const csvContent = csvRows?.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `leads_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
      
      return { success: true, count: data?.length };
    } catch (error) {
      console.error('Error exporting leads:', error);
      throw error;
    }
  },

  // Get import history
  async getImportHistory(advisorId) {
    try {
      const { data, error } = await supabase
        ?.from('lead_imports')
        ?.select('*')
        ?.eq('imported_by', advisorId)
        ?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(record => ({
        id: record?.id,
        fileName: record?.file_name,
        totalRows: record?.total_rows,
        successfulImports: record?.successful_imports,
        failedImports: record?.failed_imports,
        duplicatesFound: record?.duplicates_found,
        status: record?.status,
        errorLog: record?.error_log,
        importSettings: record?.import_settings,
        createdAt: record?.created_at,
        completedAt: record?.completed_at
      }));
    } catch (error) {
      console.error('Error fetching import history:', error);
      throw error;
    }
  },

  // Get lead history
  async getLeadHistory(leadId) {
    try {
      const { data, error } = await supabase
        ?.from('lead_history')
        ?.select(`
          *,
          user_profiles:changed_by (
            full_name,
            email
          )
        `)
        ?.eq('lead_id', leadId)
        ?.order('changed_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(record => ({
        id: record?.id,
        leadId: record?.lead_id,
        changedBy: record?.user_profiles?.full_name || 'System',
        fieldChanged: record?.field_changed,
        oldValue: record?.old_value,
        newValue: record?.new_value,
        changeType: record?.change_type,
        changedAt: record?.changed_at,
        metadata: record?.metadata
      }));
    } catch (error) {
      console.error('Error fetching lead history:', error);
      throw error;
    }
  },

  // Get lead activities
  async getLeadActivities(leadId) {
    try {
      const { data, error } = await supabase
        ?.from('lead_activities')
        ?.select(`
          *,
          user_profiles:advisor_id (
            full_name,
            email
          )
        `)
        ?.eq('lead_id', leadId)
        ?.order('scheduled_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(activity => ({
        id: activity?.id,
        leadId: activity?.lead_id,
        advisorId: activity?.advisor_id,
        advisorName: activity?.user_profiles?.full_name,
        activityType: activity?.activity_type,
        title: activity?.title,
        description: activity?.description,
        scheduledAt: activity?.scheduled_at,
        completedAt: activity?.completed_at,
        status: activity?.status,
        priority: activity?.priority,
        outcome: activity?.outcome,
        createdAt: activity?.created_at,
        updatedAt: activity?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching lead activities:', error);
      throw error;
    }
  },

  // Create lead activity
  async createActivity(activityData, advisorId) {
    try {
      const { data, error } = await supabase
        ?.from('lead_activities')
        ?.insert({
          lead_id: activityData?.leadId,
          advisor_id: advisorId,
          activity_type: activityData?.activityType,
          title: activityData?.title,
          description: activityData?.description,
          scheduled_at: activityData?.scheduledAt,
          status: activityData?.status || 'pending',
          priority: activityData?.priority || 'medium'
        })
        ?.select()
        ?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        leadId: data?.lead_id,
        advisorId: data?.advisor_id,
        activityType: data?.activity_type,
        title: data?.title,
        description: data?.description,
        scheduledAt: data?.scheduled_at,
        status: data?.status,
        priority: data?.priority,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating activity:', error);
      throw error;
    }
  },

  // Update activity status
  async updateActivityStatus(activityId, status, outcome = null) {
    try {
      const updateData = {
        status,
        outcome
      };
      
      if (status === 'completed') {
        updateData.completed_at = new Date()?.toISOString();
      }
      
      const { data, error } = await supabase
        ?.from('lead_activities')
        ?.update(updateData)
        ?.eq('id', activityId)
        ?.select()
        ?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating activity:', error);
      throw error;
    }
  }
};